import notificationService from './notificationService'
import { notificationTypes } from '../components/Default/NotificationCenter/constants'
import { evaluatePublishInfo } from './publishInfoRules'

// Single writer of the publish-delay and low-update-frequency notifications.
// The two are mutually exclusive (one needs unpublished changes, the other
// needs none), so pushing one always dismisses the other.
const NOTIFICATION_IDS = {
  [notificationTypes.PUBLISH_DELAY]: 'publish-delay',
  [notificationTypes.LOW_UPDATE_FREQUENCY]: 'low-update-frequency',
}

let lastSignature = null
// cached so either input changing can re-evaluate without the other.
let lastPublishInfo = null
let canPublish = true

const evaluate = () => {
  const result = evaluatePublishInfo(lastPublishInfo, Date.now(), { canPublish })
  const signature = result ? `${result.type}|${result.dueAt}` : ''

  if (signature === lastSignature) {
    return
  }
  lastSignature = signature

  Object.entries(NOTIFICATION_IDS).forEach(([type, id]) => {
    if (type !== result?.type) {
      notificationService.dismiss(id)
    }
  })

  if (result) {
    notificationService.push({
      id: NOTIFICATION_IDS[result.type],
      type: result.type,
      createdAt: result.dueAt,
    })
  }
}

/**
 * Evaluates the property's publish info and pushes/dismisses the publish-info
 * notifications accordingly.
 *
 * Guards on a signature of the result so re-running with unchanged publish info
 * is a no-op (the notification isn't needlessly re-pushed / re-marked unread).
 *
 * @param {{ publishedAt: ?string, updatedAt: ?string }} publishInfo
 */
export const syncPublishInfoNotifications = (publishInfo) => {
  lastPublishInfo = publishInfo
  evaluate()
}

/**
 * Feeds usePublishGuard's `canPublish` in, so the publish-delay notification is
 * hidden while the publish button is disabled.
 *
 * @param {boolean} value
 */
export const setPublishInfoCanPublish = (value) => {
  if (canPublish === value) {
    return
  }
  canPublish = value
  if (lastPublishInfo) {
    evaluate()
  }
}

/**
 * Clears the notifications and the signature tracker — call when leaving a
 * property or switching to another one, so the next property re-emits its own
 * state. `canPublish` is kept: it mirrors the live guard, not the property.
 */
export const resetPublishInfoNotifications = () => {
  lastSignature = null
  lastPublishInfo = null
  Object.values(NOTIFICATION_IDS).forEach(id => notificationService.dismiss(id))
}
